import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomButton from "../../../../components/CustomButton";
import CustomSelect from "../../../../components/Select/Select";
import { AppColors } from "../../../../util/AppColors";
import PropTypes from "prop-types";

// Shared static constant for destination options
const FACILITY_OPTIONS = [
  { name: "Select...", value: "" },
  { name: "Consult Room 1", value: "Consult Room 1" },
  { name: "Consult Room 2", value: "Consult Room 2" },
  { name: "Surgery", value: "Surgery" },
  { name: "OT", value: "OT" }
];

const TransferFacilityModal = ({ open, onClose, record, onTransfer }) => {
  const [selectedFacility, setSelectedFacility] = useState("");

  if (!record) return null;

  // Determine items list to display
  let transferItems = [];
  if (record.id === 1) {
    transferItems = [
      { name: "Pain relief", quantity: 1, from: "Pharmacy" },
      { name: "Syringe 5ml", quantity: 2, from: "Main store" }
    ];
  } else if (record.itemsList) {
    transferItems = record.itemsList
      .filter((item) => item.action === "Transfer" || !item.action)
      .map((item) => ({
        name: item.name,
        quantity: item.qty || 1,
        from: item.from || "Pharmacy" // Default location if not specified
      }));
  }

  // Fallback if no items are found
  if (transferItems.length === 0 && record.transferCount > 0) {
    transferItems = [
      { name: "Prescribed items", quantity: record.transferCount, from: "Pharmacy" }
    ];
  }

  const handleFacilityChange = (e) => {
    setSelectedFacility(e.target.value);
  };

  const handleClose = () => {
    setSelectedFacility("");
    onClose();
  };

  return (
    <CustomModal
      open={open}
      onClose={handleClose}
      header="Transfer to facility"
      modal
      modalWidth="35"
      headerShown
      headerBlack
      headerBold
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, px: 1, pb: 1 }}>
        
        {/* Items to transfer section */}
        <Box>
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontWeight: "bold",
              color: AppColors.black,
              mb: 1
            }}
          >
            Items to transfer
          </Typography>

          <Box
            sx={{
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              padding: "16px",
              border: "1px solid #e2e2e2",
              display: "flex",
              flexDirection: "column",
              gap: 1.5
            }}
          >
            {transferItems.map((item, index) => (
              <Box key={index}>
                <Typography
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: "14px",
                    color: AppColors.black,
                    fontWeight: 500,
                    lineHeight: 1.2
                  }}
                >
                  • {item.name} ({item.quantity})
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: "12px",
                    color: AppColors.gray3,
                    pl: 2.2,
                    mt: 0.2
                  }}
                >
                  From: {item.from}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Informational description */}
        <Typography
          sx={{
            fontFamily: "Montserrat",
            fontSize: "12px",
            color: AppColors.gray3,
            lineHeight: 1.4
          }}
        >
          Items may be from different locations. Select destination below. For mixed sources, complete from the case detail page if needed.
        </Typography>

        {/* Dropdown selector */}
        <Box>
          <CustomSelect
            label="Transfer to (facility / room)"
            name="transferFacility"
            list={FACILITY_OPTIONS}
            value={selectedFacility}
            handleChange={handleFacilityChange}
            select
          />
        </Box>

        {/* Footer actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 1 }}>
          <Box sx={{ width: "100px" }}>
            <CustomButton
              text="Cancel"
              smallBtn
              whiteBtn
              onClick={handleClose}
            />
          </Box>
          <Box sx={{ width: "100px" }}>
            <CustomButton
              text="Transfer"
              smallBtn
              disabled={!selectedFacility}
              onClick={() => {
                if (onTransfer) onTransfer(record, selectedFacility);
                handleClose();
              }}
            />
          </Box>
        </Box>
      </Box>
    </CustomModal>
  );
};

TransferFacilityModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  record: PropTypes.object,
  onTransfer: PropTypes.func
};

export default TransferFacilityModal;
